<template>
  <div class="ticket">
    <div class="ticket__inner">
      <div class="ticket__heading">
        <div class="ticket__heading__left">
          <div class="ticket__heading__burger">
            <i
              class="ri-menu-5-line"
              @click="openMenu"
            />
          </div>
          <div class="ticket__heading__user">
            <div class="ticket__heading__user__username" :title="user.name + ' ' + user.surname">
              {{ user.name + ' ' + user.surname }}
            </div>
            <div class="ticket__heading__user__role">
              {{ $store.userRole.name | translate }}
            </div>
          </div>
        </div>
        <div class="ticket__heading__center">
          <div class="ticket__heading__window">
            {{ windowType }} {{ device.window ? device.window.number : '' }}
          </div>
        </div>
        <div class="ticket__heading__right">
          <div class="ticket__heading__td">
            <div class="ticket__heading__right__time">
              {{ time }}
            </div>
            <div class="ticket__heading__right__date">
              {{ date }}
            </div>
          </div>
          <div class="ticket__heading__tickets" @click="openMobileTickets">
            <span>{{ $store.waitingTickets.length }}</span>
            <i class="ri-group-line"></i>
          </div>
        </div>
      </div>
      <div
        class="ticket__body"
      >
        <div class="ticket__body__window">
          {{ windowType }} {{ device.window ? device.window.number : '' }}
        </div>
        <template v-if="ticketServicing && ticket">
          <div class="ticket__body__ticket">
            <div
              class="ticket-info"
              :class="{'is--legacy': !this.service}"
            >
              <div class="ticket-info__number">
                <div class="ticket-info__number__icon">
                  <i class="ri-coupon-3-line" />
                </div>
                <div class="ticket-info__number__num">
                  {{ ticket.prefix + ticket.number }}
                </div>
              </div>
              <div
                v-if="phone"
              >
                {{ phone }}
              </div>
              <div
                v-if="name || surname"
                class="ticket-info__description"
              >
                {{ name }} {{ surname }}
              </div>
              <div class="ticket-info__time">
                <Timer
                  :ticket-time="ticketServicing.start_time"
                  :critical-time="((service && service.time_limit ? service.time_limit : 0) * 75) / 100"
                />
              </div>
              <div
                v-if="serviceTitle"
                :title="serviceTitle"
                class="ticket-info__description"
              >
                {{ serviceTitle }}
              </div>
              <div
                v-if="reglamentTime"
                class="ticket-info__reglament-time"
              >
                <i class="ri-timer-line"></i>
                <span>{{ reglamentTime }}</span>
              </div>
              <TicketFullDescription v-if="ticket && ticket.additional_info" :info="ticket.additional_info"/>
              <template v-if="lastAction && lastAction.status">
                <div class="ticket-info__action is--purple" v-if="lastAction.status === ticketStatuses.REDIRECTED">
                  <i class="ri-arrow-go-forward-fill" />
                  {{ 'd_ws_ticket_status_redirected' | translate({name: `${lastAction.user.name} ${lastAction.user.surname}`}) }}
                </div>
                <div class="ticket-info__action is--teal" v-else-if="lastAction.status === ticketStatuses.POSTPONED">
                  <i class="ri-pause-circle-line"></i>
                  {{ 'd_ws_ticket_status_postponed' | translate }}
                </div>
                <div class="ticket-info__action is--pink" v-else-if="lastAction.status === ticketStatuses.CANCELED">
                  <i class="ri-close-circle-line"></i>
                  {{ 'd_ws_ticket_status_canceled' | translate }}
                </div>
              </template>
              <div class="ticket-info__tags">
                <template v-if="tags && tags.length">
                  <div
                    v-for="(tag, idx) in tags"
                    :key="tag.id"
                    class="tag"
                    :class="tag.color ? 'is--bg-' + tag.color : ''"
                    :title="tag.title"
                  >
                    <span class="text">{{ tag.title }}</span>
                    <i
                      class="ri-close-circle-fill"
                      @click="removeTag(idx)"
                    />
                  </div>
                  <button
                    class="is--add is--small"
                    @click="addTags"
                  >
                    <i class="ri-add-line" />
                  </button>
                </template>
                <template v-else>
                  <button
                    class="is--add"
                    @click="addTags"
                  >
                    {{ ticketActionAccess ? $options.filters.translate('d_ws_add_tag') : $options.filters.translate('d_ws_add_tag') + `(${$options.filters.translate('required_field')})` }}
                    <i class="ri-add-line" />
                  </button>
                </template>
              </div>
            </div>
          </div>
          <div class="ticket__body__footer">
            <button
              class="secondary is--w50"
              @click="ticketRecall"
              :disabled="!$store.ticketRecallAccess"
              v-if="service"
            >
              <i class="ri-notification-3-line" />
              <span class="text">{{ 'd_ws_ticket_recall' | translate }}</span>
            </button>
            <button
              class="secondary is--w50"
              @click="ticketAction('ticketAbsence')"
              :disabled="!ticketActionAccess"
              v-if="service"
            >
              <i class="ri-close-circle-line" />
              <span class="text">
                {{ 'd_ws_ticket_cancel' | translate }}
              </span>
            </button>
            <button
              class="secondary is--w50"
              @click="ticketRedirect"
              :disabled="!ticketActionAccess || !$store.ticketTransactionEnd"
              v-if="service"
            >
              <i class="ri-arrow-go-forward-fill" />
              <span class="text">{{ 'd_ws_ticket_redirect' | translate }}</span>
            </button>
            <button
              class="secondary is--w50"
              @click="ticketAction('ticketPostpone')"
              :disabled="!ticketActionAccess || !$store.ticketTransactionEnd"
              v-if="service"
            >
              <i class="ri-pause-circle-line" />
              <span class="text">
                {{ 'd_ws_ticket_postpone' | translate }}
              </span>
            </button>
            <button
              v-if="$store.isEditRoute"
              class="secondary is--w100"
              @click="openEditRouteModal"
            >
              <i class="ri-route-line" />
              <span class="text">{{ 'route_editing' | translate }}</span>
            </button>
            <button
              v-if="!$store.isIssuingPoint"
              class="primary is--w100"
              @click="ticketAction('ticketClose')"
              :disabled="!ticketActionAccess || !$store.ticketTransactionEnd"
            >
              <i class="ri-checkbox-circle-line" />
              <span class="text">{{ 'd_ws_ticket_complete' | translate }}</span>
            </button>
            <button
              v-else
              class="primary is--w100"
              @click="ticketAction('orderAssembled')"
              :disabled="!ticketActionAccess || !$store.ticketTransactionEnd"
            >
              <i class="ri-checkbox-circle-line" />
              <span class="text">{{ 'd_ws_order_assembled' | translate }}</span>
            </button>
          </div>
        </template>
        <template v-else>
          <TicketFullStatus :status="currentStatus" />
          <div class="ticket__body__footer">
            <div class="ticket__body__footer__message" v-if="!$store.waitingTickets.length && $store.currentStatus === 1">
              {{ 'd_ws_empty_tickets_message' | translate }}
            </div>
            <button
              class="secondary is--w100"
              @click="action('changeStatus')"
            >
              <span class="text">{{ 'd_ws_change_status' | translate }}</span>
            </button>
            <button
              class="primary is--w100"
              :disabled="($store.currentStatus === 1 && !$store.waitingTickets.length) || !$store.ticketTransactionEnd"
              @click="action('ticketNext')"
            >
              <i class="ri-speed-mini-line" />
              <span class="text">{{ 'd_ws_next_ticket' | translate }}</span>
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import Timer from '../controls/Timer';
import TicketFullStatus from "./TicketFullStatus";
import TicketFullDescription from "./TicketFullDescription";
import {TICKET_STATUS} from "../../config";
import * as moment from "moment";

export default {
  name: 'TicketFull',
  components: {
    Timer,
    TicketFullStatus,
    TicketFullDescription,
  },
  props: ['ticketServicing', 'ticket'],
  data() {
    return {
      time: '00:00:00',
      date: 'dd., 00.00.0000',
      ticketTime: '00:00',
      timeIsOver: false,
      timeUnsub: null
    }
  },
  computed: {
    user() {
      return this.$store.user;
    },
    phone() {
      return this.$store.currentTicket.phone;
    },
    name() {
      return this.$store.currentTicket.name
    },
    surname() {
      return this.$store.currentTicket.surname
    },
    device() {
      return this.$store.deviceStartData;
    },
    service() {
      return this.$ticketService.getCurrentService(this.ticket.office_service_id);
    },
    tags() {
      return this.$store.currentTicketTags;
    },
    currentStatus() {
      return this.$store.statuses.find(status => status.id === this.$store.currentStatus);
    },
    lastAction() {
      return this.$ticketService.getCurrentLastAction(this.ticket)
    },
    ticketStatuses() {
      return TICKET_STATUS
    },
    ticketActionAccess() {
      return this.device.unit_settings.required_use_tags ? this.device.unit_settings.required_use_tags && this.tags.length : !this.device.unit_settings.required_use_tags
    },
    reglamentTime() {
      let time = null;
      if (this.service && this.device.unit_settings.show_reglament_time_of_serve) {
        const limit = this.service.time_limit;
        const format = limit > 59 ? 'HH:mm:ss' : 'mm:ss';

        time = moment().startOf('day').minutes(limit).format(format)
      }

      return time;
    },
    serviceTitle() {
      return this.service ? this.$ticketService.getServiceTitle(this.service) : this.$languageService.translateFilter('d_ws_unknown_service');
    },
    windowType() {
      return this.$store.deviceStartData.window ? this.$options.filters.translate(`window_type_${this.$store.deviceStartData.window.type}`) : ''
    },
  },
  mounted() {
    this.timeUnsub = this.$eventEmitter.subscribe(this.$eventEmitter.eventTypes.TIME_TICK, (m) => {
      this.time = moment().format('HH:mm:ss');
      this.date = m.format('ddd., DD.MM.YYYY');
    });
  },
  destroyed() {
    if (this.timeUnsub) this.timeUnsub();
  },
  methods: {
    openMenu() {
      this.$emit('menu');
    },
    action(action) {
      this.$emit(action, this.ticketServicing);
    },
    ticketAction(action) {
      if (this.ticketActionAccess) {
        this.$emit(action, this.ticketServicing);
      }
    },
    addTags() {
      this.$modal.show(this.$MODAL_NAME.ADD_TAGS);
    },
    removeTag(idx) {
      this.$store.currentTicketTags.splice(idx, 1);
    },
    openMobileTickets() {
      this.$emit('openMobileTickets');
    },
    ticketRecall() {
      this.action('ticketRecall');
    },
    ticketRedirect() {
      if (this.ticketActionAccess) {
        this.$emit('ticketRedirect', this.ticket);
      }
    },
    openEditRouteModal() {
      this.$modal.show(this.$MODAL_NAME.EDIT_ROUTE);
    }
  }
}
</script>

<style lang="scss" scoped>
  .ticket {
    background-color: var(--light-01);
    height: 100%;
    box-shadow: 0px 33px 38px rgba(0, 0, 0, 0.0314074), 0px 12px 12px rgba(0, 0, 0, 0.05), 0px 3px 3px rgba(0, 0, 0, 0.08);
    border-radius: 6px;
    &__inner {
      height: 100%;
      display: flex;
      flex-direction: column;
    }
    &__heading {
      display: flex;
      padding: 12px 16px;
      color: var(--label-secondary);
      border-bottom: 3px dotted var(--label-quarternary);
      align-items: center;
      height: 57px;
      &__left {
        margin-right: auto;
        display: flex;
        align-items: center;
        max-width: 100%;
        overflow: hidden;
      }
      &__burger {
        margin-right: 16px;
        i {
          font-size: 24px;
          cursor: pointer;
        }
      }
      &__user {
        font-size: 12px;
        max-width: calc(100% - 40px);
        &__username {
          font-weight: 600;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
      &__right {
        margin-left: auto;
        text-align: right;
        &__time,
        &__date {
          font-size: 12px;
        }
        &__time {
          font-weight: 600;
        }
        &__date {
          text-transform: capitalize;
          white-space: nowrap;
        }
      }
      &__window {
        font-weight: 500;
        font-size: 16px;
        text-transform: uppercase;
        display: none;
      }
      &__tickets {
        display: flex;
        align-items: center;
        color: var(--label-secondary);
        display: none;
        span {
          font-weight: 500;
          font-size: 16px;
          margin-right: 4px;
        }
        i {
          font-size: 24px;
        }
      }
      &__td {
        margin-left: 20px;
      }
    }
    &__body {
      height: calc(100% - 57px);
      display: flex;
      flex-direction: column;
      padding: 12px 16px 16px 16px;
      &__window {
        text-align: center;
        font-weight: 600;
        font-size: 22px;
        line-height: 27px;
        margin: 0 0 62px 0;
        text-transform: uppercase;
      }
      &__ticket {
        .ticket-info {
          text-align: center;
          color: var(--label-primary);
          &__number {
            margin-bottom: 4px;
            &__icon {
              font-size: 32px;
              margin-bottom: 4px;
              i {
                vertical-align: middle;
              }
            }
            &__num {
              font-weight: 600;
              font-size: 28px;
              line-height: 34px;
            }
          }
          &__time {
            margin-bottom: 4px;
            font-weight: 300;
            font-size: 16px;
            line-height: 150%;
            color: var(--accent-green);
          }
          &__description {
            font-weight: 300;
            line-height: 150%;
            margin-bottom: 16px;
            -webkit-line-clamp: 4;
            overflow: hidden;
            white-space: normal;
            display: -webkit-box;
            -webkit-box-orient: vertical;
          }
          &__reglament-time {
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 8px;
            color: var(--label-secondary);
            i {
              font-size: 20px;
              margin-right: 4px;
            }
            span {
              font-weight: 600;
              font-size: 12px;
            }
          }
          &__action {
            font-weight: 600;
            font-size: 12px;
            line-height: 130%;
            margin-bottom: 12px;
            i {
              vertical-align: middle;
              font-size: 20px;
              margin-right: 4px;
              font-weight: normal;
            }
          }
          &__comment {
            color: var(--label-secondary);
            font-size: 12px;
            line-height: 130%;
            margin-bottom: 20px;
            span {
              font-weight: 600;
            }
            i {
              font-size: 20px;
              margin-right: 4px;
              position: relative;
              top: 6px;
            }
          }
          &__tags {
            display: flex;
            align-items: center;
            justify-content: center;
            flex-wrap: wrap;
            .tag {
              display: flex;
              align-items: center;
              margin-right: 8px;
              margin-bottom: 8px;
              height: 28px;
              border-radius: 8px;
              background: var(--label-quarternary);
              color: var(--label-secondary);
              font-weight: 300;
              font-size: 16px;
              white-space: nowrap;
              max-width: 100%;
              span.text {
                display: block;
                margin-right: 4px;
                margin-left: 8px;
                max-width: calc(100% - (4px + 8px + 20px));
                overflow: hidden;
                text-overflow: ellipsis;
              }
              i {
                cursor: pointer;
                color: var(--label-tetriary);
                margin-right: 2px;
                font-size: 20px;
              }
            }
            button.is--add {
              width: auto;
              padding: 0 4px 0 10px;
              background: transparent;
              color: var(--label-secondary);
              border-color: var(--label-secondary);
              height: 28px;
              &.is--small {
                padding: 0;
                width: 28px;
                margin-bottom: 8px;
                i {
                  margin: 0;
                }
              }
              i {
                margin: 0 0 0 4px;
              }
            }
          }
          &.is--legacy {
            .ticket-info__number,
            .ticket-info__description {
              color: var(--accent-pink);
            }
          }
        }
      }
      &__footer {
        display: flex;
        flex-wrap: wrap;
        margin: auto -8px 0 -8px;
        &__message {
          text-align: center;
          width: 100%;
          color: var(--label-secondary);
          font-weight: 300;
          line-height: 150%;
          max-width: 290px;
          margin: 0 auto 8px auto;
        }
        button.is--w50 {
          width: calc(50% - 16px);
          margin: 0 8px 16px 8px;
        }
        button.is--w100 {
          margin-left: 8px;
          margin-right: 8px;
          margin-bottom: 16px;
          &:last-child {
            margin-bottom: 0;
          }
        }
      }
    }
  }

  @media (max-width: 425px) {
    .ticket {
      &__heading {
        justify-content: center;
        &__user,
        &__td {
          display: none;
        }
        &__window,
        &__tickets {
          display: flex;
        }
      }
      &__body {
        &__window {
          display: none;
        }
        &__footer {
          margin-left: -4px;
          margin-right: -4px;
          button.is--w50 {
            margin-left: 4px;
            margin-right: 4px;
            width: calc(50% - 8px);
          }
          button.is--w100 {
            margin-left: 4px;
            margin-right: 4px;
          }
        }
      }
    }
  }
</style>
